/* =====================================================================
   NMSC LABS: nmsc-app.js
   Page choreography for the NMSC layout. Ported from the HTML build's main.js and reorganised so every
   section is an init/destroy module bound to its [data-nmsc-section] root:

     header       the line-diagram nav (riding marker), mobile menu, contact modal
     hero         wall map intro, rotating claim, typed console, chips, line focus
     departures   hover tilt on the product screens
     domains      draggable physics panel (Matter.js, loaded on demand)
     work         pinned fan of line cards
     preview      studio frame scales up as it enters
     capabilities card parallax
     process      the trunk fills as you read
     footer       wordmark reveal

   Sections can be added, edited and removed in the theme editor, so each module is created on load and
   reverted (gsap.context) on unload. Needs window.gsap (+ ScrollTrigger, SplitText); degrades to plain
   CSS without it. Optional: window.Lenis (smooth scroll), window.Matter (physics).
   ===================================================================== */
(function () {
  'use strict';

  var win = window;
  var doc = document;
  var html = doc.documentElement;
  var NMSC = (win.NMSC = win.NMSC || {});

  var gsap = win.gsap;
  var ScrollTrigger = win.ScrollTrigger;
  var SplitText = win.SplitText;
  var hasGsap = !!gsap;
  var hasST = hasGsap && !!ScrollTrigger;
  var hasSplit = hasGsap && !!SplitText;
  if (hasST) gsap.registerPlugin(ScrollTrigger);
  if (hasSplit) gsap.registerPlugin(SplitText);
  // Mobile URL-bar show/hide fires resize; refreshing every pin on it makes scrolling jump.
  if (hasST) ScrollTrigger.config({ ignoreMobileResize: true });

  var mq = function (q) { return win.matchMedia(q); };
  var reducedMQ = mq('(prefers-reduced-motion: reduce)');
  var finePointer = mq('(pointer: fine)').matches;
  var narrowMQ = mq('(max-width: 900px)');
  var mapMQ = mq('(min-width: 1180px)'); // the wall map (and with it the hidden-until-scrolled nav) only exists here
  var design = !!NMSC.design;

  var qs = function (s, c) { return (c || doc).querySelector(s); };
  var qsa = function (s, c) { return Array.prototype.slice.call((c || doc).querySelectorAll(s)); };
  var reduced = function () { return reducedMQ.matches; };
  var motionOff = function () { return reducedMQ.matches || NMSC.paused === true; };
  var off = function (list, fn) { list.push(fn); };

  /* ------------------------------------------------------------------
     Section module registry (init/destroy bound to [data-nmsc-section])
     ------------------------------------------------------------------ */
  var modules = {};
  function register(name, mod) { modules[name] = mod; }

  function initRoot(root) {
    var mod = modules[root.getAttribute('data-nmsc-section')];
    if (!mod || root.__nmsc) return;
    var rec = { cleanups: [] };
    root.__nmsc = rec;
    var run = function () { mod.init(root, rec); };
    if (hasGsap) rec.ctx = gsap.context(run, root);
    else run();
  }

  function destroyRoot(root) {
    var rec = root.__nmsc;
    if (!rec) return;
    rec.cleanups.forEach(function (fn) { try { fn(); } catch (e) { /* section already gone */ } });
    if (rec.ctx) rec.ctx.revert();
    delete root.__nmsc;
  }

  /* ------------------------------------------------------------------
     Scroll: optional Lenis smooth scroll, scroll lock, anchor links
     ------------------------------------------------------------------ */
  var lenis = null;
  function setupSmooth() {
    if (design || !NMSC.smooth || !win.Lenis || !hasST || reduced() || !finePointer) return;
    lenis = new win.Lenis({ lerp: 0.09, smoothWheel: true, wheelMultiplier: 1 });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
    NMSC.lenis = lenis;
  }

  var locks = 0;
  function lockScroll(on) {
    locks = Math.max(0, locks + (on ? 1 : -1));
    var locked = locks > 0;
    html.classList.toggle('nmsc-locked', locked);
    if (lenis) { if (locked) lenis.stop(); else lenis.start(); }
  }

  function scrollToEl(el, immediate) {
    if (!el) return;
    var isTop = el.id === 'top';
    if (lenis) {
      lenis.scrollTo(el, { offset: isTop ? 0 : -8, duration: 1.4, immediate: !!immediate });
    } else {
      el.scrollIntoView({ behavior: immediate || reduced() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  function samePage(url) {
    var strip = function (p) { return p.replace(/\/$/, '') || '/'; };
    return url.origin === win.location.origin && strip(url.pathname) === strip(win.location.pathname);
  }

  // Header links are #work on the home page and /#work elsewhere: match on the hash when the path is ours.
  doc.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (a.target && a.target !== '_self') return;
    var url;
    try { url = new URL(a.href, win.location.href); } catch (err) { return; }
    if (!url.hash || !samePage(url)) {
      if (a.getAttribute('href') === '#') e.preventDefault();
      return;
    }
    var id = decodeURIComponent(url.hash.slice(1));
    var target = doc.getElementById(id);
    if (!target && id === 'top') target = doc.body;
    if (!target) return;
    e.preventDefault();
    setMenu(false);
    if (target === doc.body) {
      if (lenis) lenis.scrollTo(0, { duration: 1.2 }); else win.scrollTo({ top: 0, behavior: reduced() ? 'auto' : 'smooth' });
    } else {
      scrollToEl(target);
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
    try { win.history.replaceState(null, '', url.hash); } catch (err) { /* sandboxed frame */ }
  });

  /* ------------------------------------------------------------------
     Mobile menu
     ------------------------------------------------------------------ */
  var menuOpen = false;
  var menuOpener = null;
  function setBackgroundInert(on) {
    var main = doc.getElementById('MainContent');
    var foot = qs('.nmsc-footer');
    [main, foot && (foot.closest('.shopify-section') || foot)].forEach(function (el) {
      if (el) el.inert = on;
    });
  }
  function setMenu(open) {
    var menu = doc.getElementById('nmsc-menu');
    var burger = qs('[data-menu-toggle]');
    if (!menu || !burger || open === menuOpen) return;
    menuOpen = open;
    menu.inert = !open;
    burger.setAttribute('aria-expanded', String(open));
    doc.body.classList.toggle('nmsc-menu-open', open);
    setBackgroundInert(open);
    lockScroll(open);
    if (open) {
      menuOpener = burger;
      var first = qs('a', menu);
      if (first) first.focus({ preventScroll: true });
    } else if (menuOpener) {
      menuOpener.focus({ preventScroll: true });
    }
  }
  doc.addEventListener('click', function (e) {
    var burger = e.target.closest && e.target.closest('[data-menu-toggle]');
    if (burger) setMenu(!menuOpen);
  });
  // Leaving the phone layout with the menu open would strand the scroll lock.
  narrowMQ.addEventListener('change', function (e) { if (!e.matches) setMenu(false); });

  /* ------------------------------------------------------------------
     Contact modal: a native <dialog> (focus trap, Esc, inert page) around {% form 'contact' %}
     ------------------------------------------------------------------ */
  var modalOpener = null;
  function getModal() { return doc.getElementById('nmsc-modal'); }

  function openModal(opts) {
    var modal = getModal();
    if (!modal || modal.open) return;
    opts = opts || {};
    setMenu(false);
    // Safari does not focus a button on click, so the opener comes from the click, not from activeElement.
    modalOpener = opts.trigger || (doc.activeElement && doc.activeElement !== doc.body ? doc.activeElement : null);
    var message = doc.getElementById('nmsc-f-message');
    if (message && opts.text && !message.value.trim()) message.value = opts.text;
    if (typeof modal.showModal === 'function') modal.showModal(); else modal.setAttribute('open', '');
    lockScroll(true);
    var fields = ['nmsc-f-name', 'nmsc-f-email', 'nmsc-f-message'].map(function (id) { return doc.getElementById(id); }).filter(Boolean);
    var firstEmpty = fields.filter(function (el) { return !el.value.trim(); })[0] || fields[0];
    if (firstEmpty) firstEmpty.focus({ preventScroll: true });
  }

  function closeModal() {
    var modal = getModal();
    if (!modal || !modal.open || modal.__closing) return;
    modal.__closing = true;
    var finish = function () {
      modal.classList.remove('is-closing');
      modal.__closing = false;
      if (typeof modal.close === 'function') modal.close(); else modal.removeAttribute('open');
      lockScroll(false);
      var status = qs('.nmsc-form__status, .nmsc-form__errors', modal);
      if (status) status.remove();
      if (modalOpener && doc.contains(modalOpener)) modalOpener.focus({ preventScroll: true });
    };
    if (reduced()) { finish(); return; }
    modal.classList.add('is-closing');
    var card = qs('.nmsc-modal__card', modal);
    var done = false;
    var once = function () { if (!done) { done = true; finish(); } };
    if (card) card.addEventListener('animationend', once, { once: true });
    setTimeout(once, 400);
  }
  NMSC.openModal = openModal;
  NMSC.closeModal = closeModal;

  doc.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-open-modal]');
    if (opener) {
      var text = opener.getAttribute('data-brief') || opener.getAttribute('data-subject') || '';
      openModal({ trigger: opener, text: text ? text + (opener.hasAttribute('data-subject') ? '\n\n' : '') : '' });
      return;
    }
    if (e.target.closest && e.target.closest('[data-close-modal]')) { closeModal(); return; }
    if (e.target === getModal()) closeModal(); // click on the backdrop (the dialog's own box)
  });
  doc.addEventListener('cancel', function (e) {
    if (e.target === getModal()) { e.preventDefault(); closeModal(); }
  }, true);
  doc.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && menuOpen) setMenu(false);
  });

  // The contact form posts to Shopify and comes straight back to this page: reopen the modal to show
  // the thank-you note or the validation errors, then tidy the URL.
  function restoreFormState() {
    var marker = qs('[data-nmsc-form-state]');
    if (!marker) return;
    var state = marker.getAttribute('data-nmsc-form-state');
    if (state !== 'success' && state !== 'error') return;
    openModal();
    try {
      var u = new URL(win.location.href);
      u.searchParams.delete('contact_posted');
      var hash = u.hash === '#contact_form' ? '' : u.hash;
      win.history.replaceState(null, '', u.pathname + u.search + hash);
    } catch (err) { /* ignore */ }
  }

  /* ------------------------------------------------------------------
     Pointer effects: magnetic plates, tile tilt
     ------------------------------------------------------------------ */
  function bindMagnetic(root, rec) {
    if (!finePointer || !hasGsap || reduced()) return;
    qsa('.nmsc-plate', root).forEach(function (b) {
      var move = function (e) {
        var r = b.getBoundingClientRect();
        gsap.to(b, { x: (e.clientX - r.left - r.width / 2) * 0.22, y: (e.clientY - r.top - r.height / 2) * 0.28, duration: 0.45, ease: 'power3.out', overwrite: 'auto' });
      };
      var leave = function () { gsap.to(b, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.45)', overwrite: 'auto' }); };
      b.addEventListener('mousemove', move);
      b.addEventListener('mouseleave', leave);
      off(rec.cleanups, function () { b.removeEventListener('mousemove', move); b.removeEventListener('mouseleave', leave); });
    });
  }

  function bindTilt(rows, rec) {
    if (!finePointer || !hasGsap || reduced() || !rows) return;
    var current = null;
    var reset = function (t) { gsap.to(t, { rotateY: 0, rotateX: 0, y: 0, duration: 0.8, ease: 'power3.out', overwrite: 'auto' }); };
    var move = function (e) {
      var t = e.target.closest ? e.target.closest('.nmsc-tile[data-tilt]') : null;
      if (current && current !== t) reset(current);
      current = t;
      if (!t) return;
      var r = t.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      gsap.to(t, { rotateY: px * 10, rotateX: -py * 10, y: -6, duration: 0.5, ease: 'power3.out', transformPerspective: 800, overwrite: 'auto' });
    };
    var leave = function () { if (current) { reset(current); current = null; } };
    rows.addEventListener('pointermove', move);
    rows.addEventListener('pointerleave', leave);
    off(rec.cleanups, function () { rows.removeEventListener('pointermove', move); rows.removeEventListener('pointerleave', leave); });
  }

  /* ------------------------------------------------------------------
     Reveals: fade-up blocks and masked line reveals for headlines.
     Hidden states exist only while this runs (CSS keys off .nmsc-rv), so content never depends on it.
     ------------------------------------------------------------------ */
  var EXPO = 'cubic-bezier(.16, 1, .3, 1)';

  function revealBlock(el) {
    el.classList.add('is-in');
    if (!el.animate) return;
    el.animate(
      [{ opacity: 0, transform: 'translateY(36px)' }, { opacity: 1, transform: 'none' }],
      { duration: 1100, easing: EXPO, delay: (parseFloat(el.getAttribute('data-delay')) || 0) * 1000, fill: 'backwards' }
    );
  }

  function revealHeading(el) {
    if (!hasSplit) { revealBlock(el); return; }
    var split = SplitText.create(el, { type: 'lines', linesClass: 'nmsc-line', mask: 'lines' });
    gsap.set(split.lines, { yPercent: 110 });
    el.classList.add('is-in');
    gsap.to(split.lines, {
      yPercent: 0, duration: 1.05, ease: 'expo.out', stagger: 0.09,
      onComplete: function () { split.revert(); }
    });
  }

  function setupReveals(root, rec) {
    if (design || reduced() || !win.IntersectionObserver) return;
    var blocks = qsa('[data-reveal]', root);
    var heads = qsa('[data-split]', root);
    if (!blocks.length && !heads.length) return;
    root.classList.add('nmsc-rv');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        if (en.target.hasAttribute('data-split')) revealHeading(en.target); else revealBlock(en.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    blocks.concat(heads).forEach(function (el) { io.observe(el); });
    off(rec.cleanups, function () {
      io.disconnect();
      root.classList.remove('nmsc-rv');
      blocks.concat(heads).forEach(function (el) { el.classList.remove('is-in'); });
    });
  }

  /* ------------------------------------------------------------------
     Header: line diagram, riding marker, show/hide, active section
     ------------------------------------------------------------------ */
  var nav = {
    el: null, rail: null, marker: null, stops: [], key: '', shown: false, markerShown: false,
    io: null, targets: [], hideOnHero: false, onScroll: null, cleanups: []
  };

  function stopKey(stop) {
    try {
      var h = new URL(stop.href, win.location.href).hash;
      return h ? decodeURIComponent(h.slice(1)) : '';
    } catch (e) { return ''; }
  }

  function markerTarget(stop) {
    if (!stop || !nav.rail) return null;
    var ring = qs('i', stop) || stop;
    var rr = nav.rail.getBoundingClientRect();
    var sr = ring.getBoundingClientRect();
    return { x: sr.left + sr.width / 2 - rr.left, y: sr.top + sr.height / 2 - rr.top };
  }

  function placeMarker(animate) {
    var m = nav.marker;
    var stop = nav.stops.filter(function (s) { return stopKey(s) === nav.key; })[0] || nav.stops[0];
    var t = markerTarget(stop);
    if (!m || !t) return;
    if (hasGsap) {
      if (animate && !reduced()) gsap.to(m, { x: t.x, y: t.y, rotate: 45, duration: 0.9, ease: 'back.out(1.6)', overwrite: 'auto' });
      else gsap.set(m, { x: t.x, y: t.y, rotate: 45 });
    } else {
      m.style.transform = 'translate(' + t.x + 'px,' + t.y + 'px) rotate(45deg)';
    }
  }

  function showMarker() {
    var m = nav.marker;
    if (!m || nav.markerShown) return;
    nav.markerShown = true;
    var stop = nav.stops.filter(function (s) { return stopKey(s) === nav.key; })[0] || nav.stops[0];
    var t = markerTarget(stop);
    if (!t) return;
    if (hasGsap && !reduced()) {
      gsap.set(m, { x: t.x - 120, y: t.y, rotate: 45, opacity: 1 });
      gsap.to(m, { x: t.x, duration: 1.1, ease: 'back.out(1.6)' });
    } else {
      placeMarker(false);
      m.style.opacity = 1;
    }
  }

  function setActive(key) {
    nav.key = key;
    nav.stops.forEach(function (s) {
      var on = stopKey(s) === key;
      s.classList.toggle('is-active', on);
      if (on) s.setAttribute('aria-current', 'location'); else s.removeAttribute('aria-current');
    });
    placeMarker(nav.markerShown);
  }

  function updateNavVisibility() {
    if (!nav.el) return;
    var hero = qs('[data-nmsc-section="hero"]');
    nav.hideOnHero = !!(NMSC.home && hero && mapMQ.matches);
    var y = lenis ? lenis.scroll : (win.pageYOffset || 0);
    var show = !nav.hideOnHero || y > Math.max(200, (hero ? hero.offsetHeight : 400) - 120);
    nav.el.classList.toggle('is-scrolled', show);
    if (show && !nav.shown) { nav.shown = true; showMarker(); }
  }

  // The active stop is the section whose top has passed the middle of the viewport (the last one, if several
  // have). Recomputed from geometry whenever any section crosses that line, so a jump back up the page
  // (anchor links, the browser's back button) lands on the right station instead of replaying every exit.
  function rebuildNavTracking() {
    if (nav.io) nav.io.disconnect();
    nav.targets = [];
    if (!nav.el) return;
    nav.stops.forEach(function (s) {
      var key = stopKey(s);
      var el = key ? doc.getElementById(key) : null;
      if (el) nav.targets.push({ key: key, el: el });
    });
    if (!nav.targets.length) return;
    updateActive();
    if (!win.IntersectionObserver) return;
    nav.io = new IntersectionObserver(updateActive, { rootMargin: '-50% 0px -50% 0px' });
    nav.targets.forEach(function (t) { nav.io.observe(t.el); });
  }

  function updateActive() {
    var mid = (win.innerHeight || 800) / 2;
    var best = null;
    var bestTop = -Infinity;
    nav.targets.forEach(function (t) {
      var top = t.el.getBoundingClientRect().top;
      if (top <= mid && top > bestTop) { best = t; bestTop = top; }
    });
    var key = best ? best.key : nav.targets[0].key;
    if (key !== nav.key) setActive(key);
  }

  function refreshNav() {
    nav.cleanups.forEach(function (fn) { fn(); });
    nav.cleanups = [];
    nav.el = qs('#nmsc-nav');
    if (!nav.el) return;
    nav.rail = qs('#nmsc-rail', nav.el);
    nav.marker = qs('#nmsc-rail-marker', nav.el);
    nav.stops = qsa('.nmsc-stop', nav.el);
    // On pages without these sections (a policy page, the 404) no stop is "here": no marker, no active link.
    var onPage = nav.stops.some(function (s) { var k = stopKey(s); return k && doc.getElementById(k); });
    if (nav.marker) nav.marker.hidden = !onPage;
    if (!onPage) {
      nav.key = '';
      nav.stops.forEach(function (s) { s.classList.remove('is-active'); s.removeAttribute('aria-current'); });
      if (nav.io) nav.io.disconnect();
      nav.targets = [];
      updateNavVisibility();
      return;
    }
    if (!nav.key && nav.stops[0]) nav.key = stopKey(nav.stops[0]);
    setActive(nav.key);
    rebuildNavTracking();
    updateNavVisibility();
    var onScroll = function () { updateNavVisibility(); };
    var onResize = function () { placeMarker(false); updateNavVisibility(); };
    win.addEventListener('scroll', onScroll, { passive: true });
    win.addEventListener('resize', onResize);
    nav.cleanups.push(function () { win.removeEventListener('scroll', onScroll); win.removeEventListener('resize', onResize); });
  }

  register('header', {
    init: function (root, rec) {
      refreshNav();
      bindMagnetic(root, rec);
    },
    destroy: function () {}
  });

  /* ------------------------------------------------------------------
     Preloader (home only, first visit of a session; nmsc-head.liquid decides before first paint)
     ------------------------------------------------------------------ */
  function runPreloader(done) {
    var pre = doc.getElementById('nmsc-preloader');
    if (!pre) { done(); return; }
    var skip = html.classList.contains('nmsc-no-pre') || !hasGsap || win.self !== win.top;
    if (skip) { pre.remove(); done(); return; }
    lockScroll(true);
    var letters = qsa('.nmsc-preloader__word span', pre);
    var bar = qs('.nmsc-preloader__bar i', pre);
    letters.forEach(function (l) { l.style.animation = 'none'; });
    pre.style.animation = 'none';
    try { win.sessionStorage.setItem('nmsc-pre', '1'); } catch (e) { /* private mode */ }
    gsap.timeline({
      onComplete: function () { pre.remove(); lockScroll(false); done(); }
    })
      .fromTo(letters, { yPercent: 110 }, { yPercent: 0, duration: 0.8, ease: 'expo.out', stagger: 0.055 })
      .to(bar, { scaleX: 1, duration: 0.85, ease: 'power2.inOut' }, 0.1)
      .to(letters, { yPercent: -110, duration: 0.55, ease: 'expo.in', stagger: 0.04 }, '-=0.2')
      .to(pre, { yPercent: -100, duration: 0.85, ease: 'expo.inOut' }, '-=0.25');
  }

  /* ------------------------------------------------------------------
     Hero: console typing, rotating claim, line focus, chips, intro
     ------------------------------------------------------------------ */
  function lines(text) {
    return (text || '').split(/\n+/).map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function consoleLoop(root, rec) {
    var typed = qs('.nmsc-typed', root);
    var briefs = lines(root.getAttribute('data-briefs'));
    if (!typed || !briefs.length) return;
    var state = { active: true, visible: true, timer: 0 };
    var wait = function (fn, ms) { state.timer = setTimeout(fn, ms); };
    var ready = function () { return state.active && state.visible && !doc.hidden && !motionOff(); };

    if (win.IntersectionObserver) {
      var io = new IntersectionObserver(function (en) { state.visible = en[en.length - 1].isIntersecting; });
      io.observe(root);
      off(rec.cleanups, function () { io.disconnect(); });
    }

    var type = function (text, i, then) {
      if (!state.active) return;
      if (!ready()) { wait(function () { type(text, i, then); }, 400); return; }
      if (i <= text.length) {
        typed.textContent = text.slice(0, i);
        wait(function () { type(text, i + 1, then); }, 18 + Math.random() * 30);
      } else {
        wait(then, 2200);
      }
    };
    var erase = function (then) {
      if (!state.active) return;
      if (!ready()) { wait(function () { erase(then); }, 400); return; }
      var t = typed.textContent;
      if (t.length) { typed.textContent = t.slice(0, -3); wait(function () { erase(then); }, 10); } else then();
    };
    var bi = 0;
    var next = function () {
      type(briefs[bi], 0, function () { erase(function () { bi = (bi + 1) % briefs.length; next(); }); });
    };
    if (!reduced()) {
      // The first brief is already in the markup (it is what no-JS and reduced-motion visitors read).
      typed.textContent = briefs[0];
      wait(function () { erase(function () { bi = 1 % briefs.length; next(); }); }, 2600);
    }

    var chips = qsa('.nmsc-chip-btn', root);
    chips.forEach(function (chip) {
      var click = function () {
        state.active = false;
        clearTimeout(state.timer);
        typed.textContent = chip.getAttribute('data-brief') || chip.textContent;
      };
      chip.addEventListener('click', click);
      off(rec.cleanups, function () { chip.removeEventListener('click', click); });
    });
    off(rec.cleanups, function () { state.active = false; clearTimeout(state.timer); });
  }

  function claimRotation(root, rec) {
    var hl = qs('#nmsc-hl-word', root);
    var words = lines(root.getAttribute('data-words'));
    if (!hl || words.length < 2 || !hasGsap || reduced()) return;
    var wi = 0;
    var visible = true;
    if (win.IntersectionObserver) {
      var io = new IntersectionObserver(function (en) { visible = en[en.length - 1].isIntersecting; });
      io.observe(root);
      off(rec.cleanups, function () { io.disconnect(); });
    }
    var timer = setInterval(function () {
      if (!visible || doc.hidden || motionOff()) return;
      wi = (wi + 1) % words.length;
      gsap.to(hl, {
        yPercent: -50, opacity: 0, duration: 0.32, ease: 'power2.in',
        onComplete: function () {
          hl.textContent = words[wi];
          gsap.fromTo(hl, { yPercent: 50, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.5, ease: 'power3.out' });
        }
      });
    }, 3200);
    off(rec.cleanups, function () { clearInterval(timer); });
  }

  function mapFocus(root, rec) {
    var svg = qs('.nmsc-map__svg', root);
    var terms = qsa('.nmsc-term', root);
    if (!svg || !terms.length) return;
    terms.forEach(function (t) {
      var id = t.getAttribute('data-line');
      var enter = function () {
        svg.classList.add('is-focus');
        qsa('.nmsc-rail', svg).forEach(function (p) { p.classList.toggle('is-hot', p.getAttribute('data-line') === id); });
      };
      var leave = function () {
        svg.classList.remove('is-focus');
        qsa('.nmsc-rail', svg).forEach(function (p) { p.classList.remove('is-hot'); });
      };
      t.addEventListener('mouseenter', enter);
      t.addEventListener('mouseleave', leave);
      off(rec.cleanups, function () { t.removeEventListener('mouseenter', enter); t.removeEventListener('mouseleave', leave); });
    });
  }

  // The cartouche lands, the map draws itself, the diamond drops. Only on the wide stage.
  function heroIntro(root) {
    var armed = html.classList.contains('nmsc-intro');
    if (!armed) return;
    if (!hasGsap || reduced() || !root.isConnected) { html.classList.remove('nmsc-intro'); return; }
    var q = function (s) { return qsa(s, root); };
    var tl = gsap.timeline({ defaults: { ease: 'expo.out' } });

    tl.from(q('.nmsc-cart'), { y: 24, opacity: 0, duration: 1 }, 0)
      .from(q('.nmsc-wordmark__letter'), { yPercent: 110, duration: 1.1, stagger: 0.07 }, 0.1)
      .from(q('.nmsc-wordmark__orb'), { scale: 0, opacity: 0, duration: 1, ease: 'back.out(1.5)' }, 0.5)
      .from(q('.nmsc-cart__labs span'), { y: 14, opacity: 0, duration: 0.7, stagger: 0.06 }, 0.55)
      .from(q('.nmsc-cart__note'), { opacity: 0, duration: 0.7 }, 0.7);

    var title = qs('#nmsc-hero-title', root);
    if (title && hasSplit) {
      var split = SplitText.create(title, { type: 'lines', linesClass: 'nmsc-line', mask: 'lines' });
      tl.from(split.lines, { yPercent: 110, duration: 1, stagger: 0.09 }, 0.6);
      tl.call(function () { split.revert(); }, null, 1.8);
    } else if (title) {
      tl.from(title, { y: 20, opacity: 0, duration: 1 }, 0.6);
    }
    tl.from(q('.nmsc-cart__line'), { y: 16, opacity: 0, duration: 0.9 }, 0.8)
      .from(q('.nmsc-strip span'), { y: -10, opacity: 0, duration: 0.8, stagger: 0.08 }, 0.4);

    var paths = q('.nmsc-map .nmsc-rail');
    if (paths.length) {
      paths.forEach(function (p) {
        var len = p.getTotalLength();
        p.style.strokeDasharray = len + ' ' + len;
        p.style.strokeDashoffset = len;
      });
      tl.to(paths, { strokeDashoffset: 0, duration: 1.9, ease: 'power2.inOut', stagger: 0.07 }, 0.3)
        .add(function () { paths.forEach(function (p) { p.style.strokeDasharray = ''; p.style.strokeDashoffset = ''; }); }, 2.6);
      gsap.set(q('.nmsc-map__origin, .nmsc-map__bars rect'), { transformOrigin: '50% 50%', transformBox: 'fill-box' });
      tl.from(q('.nmsc-map__origin'), { scale: 0, duration: 0.8, ease: 'back.out(1.6)' }, 0.2)
        .from(q('.nmsc-map__bars rect'), { scaleY: 0, duration: 0.7, ease: 'back.out(1.4)', stagger: 0.08 }, 1.0)
        .from(q('.nmsc-stn'), { y: 12, opacity: 0, duration: 0.8, stagger: 0.08 }, 1.2)
        .from(q('.nmsc-term'), { x: 24, opacity: 0, duration: 0.9, stagger: 0.07 }, 1.6)
        .from(q('.nmsc-origin'), { y: 20, opacity: 0, duration: 0.9 }, 1.0)
        .from(q('.nmsc-legend-box'), { y: 14, opacity: 0, duration: 0.8 }, 1.9)
        .from(q('.nmsc-hero__cta'), { y: 14, opacity: 0, duration: 0.8 }, 2.0)
        .fromTo(q('#nmsc-map-marker'), { y: -60, opacity: 0 }, { y: 0, opacity: 1, duration: 1.1, ease: 'back.out(1.8)', clearProps: 'y,opacity' }, 1.5)
        .from(q('.nmsc-here'), { opacity: 0, duration: 0.6 }, 1.9);
    }
    // The stage is hidden by CSS until now; the tweens above already hold their start states.
    html.classList.remove('nmsc-intro');
  }

  register('hero', {
    init: function (root, rec) {
      consoleLoop(root, rec);
      claimRotation(root, rec);
      mapFocus(root, rec);
      bindMagnetic(root, rec);
      NMSC.heroRoot = root;
      off(rec.cleanups, function () { if (NMSC.heroRoot === root) NMSC.heroRoot = null; });
    },
    destroy: function () {}
  });

  /* ------------------------------------------------------------------
     Departures board
     ------------------------------------------------------------------ */
  register('departures', {
    init: function (root, rec) {
      setupReveals(root, rec);
      bindTilt(qs('.nmsc-board__rows', root), rec);
    }
  });

  register('statement', { init: setupReveals });
  register('contact', { init: function (root, rec) { setupReveals(root, rec); bindMagnetic(root, rec); } });
  register('page', { init: setupReveals });
  register('notfound', { init: function (root, rec) { setupReveals(root, rec); bindMagnetic(root, rec); } });

  /* ------------------------------------------------------------------
     Studio preview: the frame scales up as it enters
     ------------------------------------------------------------------ */
  register('preview', {
    init: function (root, rec) {
      setupReveals(root, rec);
      if (!hasST || reduced() || design) return;
      var frame = qs('.nmsc-preview__frame', root);
      if (!frame) return;
      gsap.set(root, { perspective: 1600 });
      gsap.fromTo(frame, { scale: 0.9, y: 60, rotateX: 6 }, {
        scale: 1, y: 0, rotateX: 0, ease: 'none',
        scrollTrigger: { trigger: root, start: 'top 85%', end: 'top 15%', scrub: 0.4 }
      });
    }
  });

  /* ------------------------------------------------------------------
     Work: pinned fan of line cards (wide screens, motion allowed)
     ------------------------------------------------------------------ */
  register('work', {
    init: function (root, rec) {
      setupReveals(root, rec);
      if (!hasST) return;
      var staticLayout = html.classList.contains('nmsc-static');
      var mm = gsap.matchMedia();
      rec.mm = mm;
      off(rec.cleanups, function () { mm.revert(); });
      if (!staticLayout) {
        mm.add('(min-width: 901px)', function () {
          var cards = qsa('.nmsc-fan__card', root);
          var n = cards.length;
          if (!n) return;
          var progress = qs('#nmsc-fan-progress', root);
          var count = qs('#nmsc-fan-count', root);
          var spreadX = Math.min(34, Math.max(22, win.innerWidth * 0.022));
          var spreadR = 5.5;
          var pad = function (v) { return String(v).padStart(2, '0'); };
          cards.forEach(function (c, i) { gsap.set(c, { zIndex: n - i, rotate: 0, x: 0, y: 40, scale: 0.94, opacity: 0 }); });
          var tl = gsap.timeline({
            scrollTrigger: {
              trigger: qs('.nmsc-pinned__inner', root), start: 'top top', end: 'bottom bottom', scrub: 0.6,
              onUpdate: function (self) {
                if (progress) progress.style.setProperty('--p', self.progress.toFixed(3));
                if (count) count.textContent = pad(Math.min(n, Math.max(1, Math.round(self.progress * (n - 1)) + 1))) + ' / ' + pad(n);
              }
            }
          });
          tl.to(cards, { opacity: 1, y: 0, scale: 1, duration: 0.25, stagger: 0.03, ease: 'power2.out' }, 0);
          cards.forEach(function (c, i) {
            tl.to(c, { rotate: i * spreadR, x: i * spreadX, y: i * 5, duration: 0.75, ease: 'power1.inOut' }, 0.25);
          });
          tl.to(cards, { y: function (i) { return i * 5 - 12; }, duration: 0.15, ease: 'power1.inOut' }, 0.95);
          return function () { tl.kill(); };
        });
      }
      mm.add('(max-width: 900px)', function () {
        if (design || reduced()) return;
        qsa('.nmsc-fan__card', root).forEach(function (c, i) {
          gsap.from(c, { y: 40, opacity: 0, duration: 0.9, ease: 'expo.out', delay: (i % 2) * 0.08, scrollTrigger: { trigger: c, start: 'top 90%' } });
        });
      });
    }
  });

  /* ------------------------------------------------------------------
     Capabilities: cards drift while their block scrolls by
     ------------------------------------------------------------------ */
  register('capabilities', {
    init: function (root, rec) {
      setupReveals(root, rec);
      if (!hasST || design || reduced()) return;
      var mm = gsap.matchMedia();
      off(rec.cleanups, function () { mm.revert(); });
      mm.add('(min-width: 901px)', function () {
        qsa('.nmsc-feature', root).forEach(function (f) {
          var card = qs('.nmsc-feature__card', f);
          if (!card) return;
          gsap.fromTo(card, { y: 90, rotate: -1.5 }, {
            y: -90, rotate: 1.5, ease: 'none',
            scrollTrigger: { trigger: f, start: 'top bottom', end: 'bottom top', scrub: true }
          });
        });
      });
    }
  });

  /* ------------------------------------------------------------------
     Process: the trunk fills and lights each station
     ------------------------------------------------------------------ */
  register('process', {
    init: function (root, rec) {
      setupReveals(root, rec);
      var line = qs('#nmsc-process-line', root);
      var steps = qsa('.nmsc-step', root);
      if (!line) return;
      if (!hasST || reduced() || design) {
        line.style.transform = 'scaleX(1)';
        steps.forEach(function (s) { s.classList.add('is-lit'); });
        return;
      }
      gsap.to(line, {
        scaleX: 1, ease: 'none',
        scrollTrigger: {
          trigger: qs('.nmsc-steps', root), start: 'top 85%', end: 'bottom 55%', scrub: 0.5,
          onUpdate: function (self) {
            steps.forEach(function (s, i) { s.classList.toggle('is-lit', self.progress >= (i + 0.35) / steps.length); });
          }
        }
      });
    }
  });

  /* ------------------------------------------------------------------
     Domains: draggable physics panel. Matter.js and nmsc-physics.js load when the panel nears view; until
     then (and always without them) the pieces sit in the frame as a static arrangement.
     ------------------------------------------------------------------ */
  var scriptPromises = {};
  function loadScript(src) {
    if (!scriptPromises[src]) {
      scriptPromises[src] = new Promise(function (resolve, reject) {
        var s = doc.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        doc.head.appendChild(s);
      });
    }
    return scriptPromises[src];
  }

  register('domains', {
    init: function (root, rec) {
      setupReveals(root, rec);
      var container = qs('#nmsc-physics', root);
      var assets = NMSC.assets || {};
      if (!container || !assets.matter || !assets.physics || reduced() || design || !win.IntersectionObserver) return;
      var world = null;
      var started = false;
      var replay = qs('[data-physics-replay]', root);
      var start = function () {
        if (started) return;
        started = true;
        loadScript(assets.matter)
          .then(function () { return loadScript(assets.physics); })
          .then(function () {
            if (!root.isConnected || !win.NMSCPhysics) return;
            world = win.NMSCPhysics.init(container);
            if (world && replay) replay.hidden = false;
          })
          .catch(function () { /* the static arrangement stays */ });
      };
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { start(); io.disconnect(); }
      }, { rootMargin: '400px 0px' });
      io.observe(container);
      var click = function () { if (world) world.replay(); };
      if (replay) replay.addEventListener('click', click);
      off(rec.cleanups, function () {
        io.disconnect();
        if (replay) replay.removeEventListener('click', click);
        if (world) world.destroy();
      });
    }
  });

  /* ------------------------------------------------------------------
     Footer: wordmark reveal, motion toggle
     ------------------------------------------------------------------ */
  register('footer', {
    init: function (root, rec) {
      var word = qsa('.nmsc-footer__word span', root);
      var orb = qs('.nmsc-footer__orb', root);
      if (hasGsap && !design && !reduced() && win.IntersectionObserver && word.length) {
        gsap.set(word, { yPercent: 110 });
        if (orb) gsap.set(orb, { scale: 0.6, opacity: 0 });
        var io = new IntersectionObserver(function (entries) {
          if (!entries.some(function (e) { return e.isIntersecting; })) return;
          io.disconnect();
          gsap.to(word, { yPercent: 0, duration: 1.1, ease: 'expo.out', stagger: 0.06 });
          if (orb) gsap.to(orb, { scale: 1, opacity: 1, duration: 1.2, ease: 'back.out(1.4)' });
        }, { rootMargin: '0px 0px -8% 0px' });
        io.observe(root);
        off(rec.cleanups, function () { io.disconnect(); });
      }
      var toggle = qs('[data-motion-toggle]', root);
      if (toggle) {
        toggle.hidden = false;
        var click = function () {
          NMSC.paused = !NMSC.paused;
          html.classList.toggle('nmsc-paused', NMSC.paused);
          toggle.setAttribute('aria-pressed', String(NMSC.paused));
          toggle.textContent = NMSC.paused ? toggle.getAttribute('data-label-off') : toggle.getAttribute('data-label-on');
        };
        toggle.addEventListener('click', click);
        off(rec.cleanups, function () { toggle.removeEventListener('click', click); });
      }
    }
  });

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function initAll(scope) {
    qsa('[data-nmsc-section]', scope).forEach(initRoot);
  }

  function refreshAll() {
    refreshNav();
    if (hasST) ScrollTrigger.refresh();
  }
  NMSC.refresh = refreshAll;

  function boot() {
    setupSmooth();
    // Static layout for the pinned section while editing or when motion is reduced.
    if (design || reduced()) html.classList.add('nmsc-static');

    var fontsReady = doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve();
    var timeout = new Promise(function (res) { setTimeout(res, 1500); });
    var ready = Promise.race([fontsReady, timeout]).then(function () {
      initAll(doc);
      if (win.NMSCOrb && NMSC.orb !== false) win.NMSCOrb.mountAll(doc);
      refreshAll();
      restoreFormState();
      // Landing on /#work from another page: re-aim once pins and fonts have settled.
      var hash = win.location.hash;
      if (hash.length > 1) {
        var target = doc.getElementById(decodeURIComponent(hash.slice(1)));
        if (target) win.addEventListener('load', function () { scrollToEl(target, true); }, { once: true });
      }
    });

    runPreloader(function () {
      ready.then(function () {
        if (NMSC.heroRoot) heroIntro(NMSC.heroRoot);
        else html.classList.remove('nmsc-intro');
      });
    });

    // Failsafe: never leave the stage hidden if anything above stalls.
    setTimeout(function () { html.classList.remove('nmsc-intro'); }, 6000);

    if (hasST) win.addEventListener('load', function () { ScrollTrigger.refresh(); placeMarker(false); }, { once: true });

    // Theme editor: sections are re-rendered in place.
    doc.addEventListener('shopify:section:load', function (e) {
      initAll(e.target);
      if (win.NMSCOrb && NMSC.orb !== false) win.NMSCOrb.mountAll(e.target);
      refreshAll();
    });
    doc.addEventListener('shopify:section:unload', function (e) {
      qsa('[data-nmsc-section]', e.target).forEach(destroyRoot);
      if (win.NMSCOrb) win.NMSCOrb.unmountAll(e.target);
      refreshAll();
    });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
